import React from 'react';
import {
  VStack,
  Button,
  ButtonText,
  Modal,
  ModalBackdrop,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Heading,
  Icon,
  Text,
  Box,
} from '@gluestack-ui/themed';
import { X } from 'lucide-react-native';
import { useForm, FormProvider } from 'react-hook-form';
import FormField from '@/common/components/forms/FormField';
import { useGetCourse } from '@/common/api/endpoints/courses/courses';
import type { CourseSegment } from '@/common/api/models/courseSegment';

interface BulkSetSegmentModalProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: (segmentId: string) => void;
  courseId: string;
  title?: string;
  description?: string;
}

const BulkSetSegmentModal: React.FC<BulkSetSegmentModalProps> = ({
  visible,
  onClose,
  onConfirm,
  courseId,
  title,
  description,
}) => {
  const { data: courseResponse } = useGetCourse(courseId);
  const segments = courseResponse?.data?.data?.segments || [];

  const methods = useForm({
    defaultValues: {
      segmentId: '',
    },
  });

  const onSubmit = (data: { segmentId: string }) => {
    onConfirm(data.segmentId);
    onClose();
  };

  const segmentChoices = segments.map((s: CourseSegment) => ({
    label: s.title,
    value: s.id,
  }));

  return (
    <Modal isOpen={visible} onClose={onClose} size="md">
      <ModalBackdrop />
      <ModalContent borderRadius="$xl" softShadow="4">
        <ModalHeader borderBottomWidth={1} borderBottomColor="$borderLight" py="$4" justifyContent="space-between" alignItems="center">
          <Heading size="md">{title || 'Set Segment for Selection'}</Heading>
          <ModalCloseButton p="$2" m="-$2">
            <Icon as={X} size="sm" color="$textSubtle" />
          </ModalCloseButton>
        </ModalHeader>
        <ModalBody py="$6">
          <FormProvider {...methods}>
            <VStack space="md">
              <Text size="sm" color="$textBody">
                {description || 'Assign selected modules to a specific segment:'}
              </Text>
              {segments.length > 0 ? (
                <FormField
                  name="segmentId"
                  label="Select Target Segment"
                  type="select"
                  choices={segmentChoices}
                  placeholder="Select a segment"
                  fieldWidth={300}
                  rules={{ required: 'Please select a segment' }}
                />
              ) : (
                <Box py="$4" px="$2" bg="$backgroundLight50" borderRadius="$md" borderWidth={1} borderColor="$borderLight" borderStyle="dashed">
                  <Text size="sm" color="$textSubtle" textAlign="center">
                    No segments defined for this course.
                  </Text>
                </Box>
              )}
            </VStack>
          </FormProvider>
        </ModalBody>
        <ModalFooter borderTopWidth={1} borderTopColor="$borderLight" py="$4">
          <Button variant="outline" action="secondary" onPress={onClose} mr="$3" size="sm">
            <ButtonText>Cancel</ButtonText>
          </Button>
          <Button
            action="primary"
            onPress={methods.handleSubmit(onSubmit)}
            size="sm"
            isDisabled={segments.length === 0}
          >
            <ButtonText>Apply</ButtonText>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default BulkSetSegmentModal;
